import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/use-toast';
import { ChevronLeft, AlertTriangle } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { locationsService } from '../../services/locationsService';
import {
  getConsumptionReport, getStockReport, getTopSellingReport,
} from '../../services/liquorManagementService';

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ml = (n) => `${Math.round(Number(n || 0)).toLocaleString()} ml`;

/** Liquor Management: consumption, stock and top-selling in one screen, with stock as a live snapshot. */
export default function LiquorManagement() {
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [locationName, setLocationName] = useState('');
  const [range, setRange] = useState({ from: daysAgoISO(30), to: todayISO() });

  // The active report is driven by ?tab=, so each report is its own URL the sidebar can link to.
  const [searchParams, setSearchParams] = useSearchParams();
  const TABS = ['consumption', 'stock', 'top'];
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'consumption';
  const setTab = useCallback((v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', v);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [consumption, setConsumption] = useState(null);
  const [stock, setStock] = useState(null);
  const [top, setTop] = useState(null);
  const [loading, setLoading] = useState(false);

  const fail = useCallback((error, what) => {
    console.error(`LiquorManagement: ${what} failed`, error);
    toast({
      title: 'Error',
      description: error?.error || error?.message || `Failed to load ${what}`,
      variant: 'destructive',
    });
  }, [toast]);

  useEffect(() => {
    if (!restaurantId || !locationId) return;
    locationsService.get(restaurantId, locationId)
      .then((loc) => setLocationName(loc?.name || ''))
      .catch(() => {});
  }, [restaurantId, locationId]);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    const params = { date_from: range.from, date_to: range.to };
    setLoading(true);
    const [c, s, t] = await Promise.allSettled([
      getConsumptionReport(restaurantId, locationId, params),
      getStockReport(restaurantId, locationId),
      getTopSellingReport(restaurantId, locationId, { ...params, limit: 20 }),
    ]);
    if (c.status === 'fulfilled') setConsumption(c.value); else fail(c.reason, 'consumption report');
    if (s.status === 'fulfilled') setStock(s.value); else fail(s.reason, 'stock report');
    if (t.status === 'fulfilled') setTop(t.value); else fail(t.reason, 'top-selling report');
    setLoading(false);
  }, [restaurantId, locationId, range.from, range.to, fail]);

  useEffect(() => { load(); }, [load]);

  const cItems = consumption?.items || [];
  const sItems = stock?.items || [];
  const tItems = useMemo(() => top?.items || [], [top]);

  const maxRevenue = useMemo(
    () => tItems.reduce((m, i) => Math.max(m, i.revenue || 0), 0),
    [tItems],
  );

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(`/restaurant/${restaurantId}/liquor-management`)}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground font-mono mb-1 hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" /> Liquor Management · {locationName || 'Location'}
        </button>
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Liquor Management</h1>
      </div>

      {/* Shared date range (Consumption + Top Selling) */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle>Reporting window</CardTitle>
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={range.from} max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={range.to} min={range.from} max={todayISO()}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-9" />
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="consumption">Consumption</TabsTrigger>
          <TabsTrigger value="stock">Stock</TabsTrigger>
          <TabsTrigger value="top">Top Selling</TabsTrigger>
        </TabsList>

        {/* ---------- Consumption ---------- */}
        <TabsContent value="consumption">
          <Card>
            <CardHeader>
              <CardTitle>Consumption</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <Metric label="Total poured" value={consumption ? ml(consumption.totals.ml_consumed) : '—'} />
                <Metric label="Cost of pours" value={consumption ? money(consumption.totals.cost_value) : '—'} />
                <Metric label="Items sold" value={consumption?.totals.items ?? '—'} />
              </div>
              <ReportTable
                loading={loading}
                rows={cItems}
                empty="No liquor poured in this window."
                head={['Item', 'Pours', 'Poured', 'Avg pour', 'Cost']}
                renderRow={(r) => (
                  <TableRow key={r.dish_id}>
                    <TableCell>
                      <div className="font-medium text-foreground">{r.name}</div>
                      {r.brand && <div className="text-xs text-muted-foreground">{r.brand}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.pours}</TableCell>
                    <TableCell className="text-right tabular-nums">{ml(r.ml_consumed)}</TableCell>
                    <TableCell className="text-right tabular-nums">{ml(r.avg_pour_ml)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.cost_value)}</TableCell>
                  </TableRow>
                )}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Stock ---------- */}
        <TabsContent value="stock">
          <Card>
            <CardHeader>
              <CardTitle>Stock on hand</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <Metric label="Items" value={stock?.totals.items ?? '—'} />
                <Metric label="Value on hand" value={stock ? money(stock.totals.value_on_hand) : '—'} />
                <Metric label="Low stock" value={stock?.totals.low_stock ?? '—'}
                  tone={stock?.totals.low_stock ? 'text-amber-600' : undefined} />
              </div>
              <ReportTable
                loading={loading}
                rows={sItems}
                empty="No liquor items offered at this location yet."
                head={['Item', 'Full', 'Open', 'Total', 'Cost/btl', 'Value']}
                renderRow={(r) => (
                  <TableRow key={r.dish_id} className={r.is_low ? 'bg-amber-50/50' : undefined}>
                    <TableCell>
                      <div className="font-medium text-foreground flex items-center gap-1.5">
                        {r.name}
                        {r.is_low && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full font-medium bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> Low
                          </span>
                        )}
                      </div>
                      {r.brand && <div className="text-xs text-muted-foreground">{r.brand}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.full_bottles}</TableCell>
                    <TableCell className="text-right tabular-nums">{ml(r.open_bottle_ml)}</TableCell>
                    <TableCell className="text-right tabular-nums">{ml(r.total_ml)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.cost_per_bottle)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.value_on_hand)}</TableCell>
                  </TableRow>
                )}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Top Selling ---------- */}
        <TabsContent value="top">
          <Card>
            <CardHeader>
              <CardTitle>Top selling</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <Metric label="Units sold" value={top?.totals.units ?? '—'} />
                <Metric label="Revenue" value={top ? money(top.totals.revenue) : '—'} />
                <Metric label="Items" value={top?.totals.items ?? '—'} />
              </div>
              <ReportTable
                loading={loading}
                rows={tItems}
                empty="No liquor sold in this window."
                head={['#', 'Item', 'Units', 'Avg price', 'Revenue', '']}
                renderRow={(r, i) => (
                  <TableRow key={r.dish_id}>
                    <TableCell className="text-muted-foreground tabular-nums">{i + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">{r.name}</div>
                      {r.brand && <div className="text-xs text-muted-foreground">{r.brand}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.units}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.avg_price)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{money(r.revenue)}</TableCell>
                    <TableCell className="w-32">
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary/70"
                          style={{ width: `${maxRevenue ? Math.max(4, (r.revenue / maxRevenue) * 100) : 0}%` }} />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReportTable({ loading, rows, head, renderRow, empty }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            {head.map((h, i) => (
              <TableHead key={i} className={i === 0 ? '' : 'text-right'}>{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && rows.length === 0 ? (
            <TableRow><TableCell colSpan={head.length} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={head.length} className="text-center py-8 text-muted-foreground">{empty}</TableCell></TableRow>
          ) : (
            rows.map((r, i) => renderRow(r, i))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div className={`text-2xl font-display font-semibold tabular-nums ${tone || 'text-foreground'}`}>{value}</div>
    </div>
  );
}
